/**
 * taskInbox — RW-2867 (RW-2857 decision C): the ONE task inbox. Deal tasks,
 * onboarding tasks and other tasks (listing / buyer / contact follow-ups) as
 * one list, so My Tasks, Home's queue and the nav badge can never disagree
 * about what is yours and when it is due.
 *
 * Where the rows come from:
 *   - deal + onboarding: dealsApi `taskInbox` (myTasks' deal rows, and the
 *     onboarding rows — staff work, plus a hire's own checklist items, by
 *     the morning digest's routing);
 *   - other tasks: the owner-scoped Firestore tasks the client already holds
 *     live (lib/taskQueries subscribeOwnedTasks).
 * Each is turned into one item shape here (fromDealRow / fromOnboardingRow /
 * fromOtherTask); everything below is pure and dated by a `today` the caller
 * passes ('YYYY-MM-DD', the viewer's local day — as My Tasks always was).
 *
 * Item: { key, source: 'deal'|'onboarding'|'task', id, title, due, status,
 *   done, check (a tick waiting on ME to check), waiting (my tick waiting on
 *   my TC), broker (a submitted gate waiting on the broker), owned, watching,
 *   place: { key, label, kind }, person: { uid, name } | null, link,
 *   canComplete, raw }
 */

import { isMyTask } from './taskOwnership';

/* ───────── dates ───────── */

const pad = (n) => String(n).padStart(2, '0');

/** The local calendar day of a Date as 'YYYY-MM-DD'. */
export function localYmd(date = new Date()) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** 'YYYY-MM-DD' plus n days (calendar math, no time zone). */
export function addDaysYmd(ymd, n) {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** Whole days from one 'YYYY-MM-DD' to another (negative when `to` is earlier). */
export function daysBetween(from, to) {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000);
}

/** "3 days overdue" · "Today" · "Tomorrow" · "Fri, Oct 9" · "No date". */
export function dueLabel(due, today) {
  if (!due) return 'No date';
  if (due < today) {
    const n = daysBetween(due, today);
    return `${n} day${n === 1 ? '' : 's'} overdue`;
  }
  if (due === today) return 'Today';
  if (due === addDaysYmd(today, 1)) return 'Tomorrow';
  const [y, m, d] = due.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

const localMidnight = (ymd) => {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).getTime();
};

const toDate = (v) => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

/* ───────── the three kinds → one item ───────── */

// RW-2858: a gate is waiting on the broker only once it has been submitted
// (and not approved since) — myDealTasks' gate_phase, the deal page's rule.
// A row with no gate_phase at all comes from a server that predates it (a
// deploy where hosting lands first): read it the old way, every open gate
// waiting, rather than call every gate unsubmitted.
const hasGatePhase = (t) => Object.prototype.hasOwnProperty.call(t, 'gate_phase');
export const isWaitingOnBroker = (t) => !!t.gate && t.status === 'open'
  && (hasGatePhase(t) ? t.gate_phase === 'submitted' : true);

/**
 * Where a deal task opens: the deal's Tasks tab, filtered so the task is in
 * view — a tick to check under "To check", a task of mine under "Mine"
 * (RW-2858), anything I only watch on the whole list ("Mine" would hide it).
 */
export function dealTaskLink(t) {
  const base = `/deals/${t.deal_id}?tab=tasks`;
  if (t.status === 'needs_review' && t.to_check === true) return `${base}&tasks=check`;
  return t.owned === true ? `${base}&tasks=mine` : base;
}

/**
 * An agent's tick waiting on ME to check it (RW-2865) — My Tasks' "To check",
 * and Deals Home's "N tasks to check" in Needs you (RW-3624).
 */
export const isDealTickToCheck = (t) => !!t && t.status === 'needs_review' && t.to_check === true;

/** A dealsApi myTasks / taskInbox deal row → an item. */
export function fromDealRow(t) {
  const ticked = t.status === 'needs_review';
  return {
    key: `deal:${t.task_id}`,
    source: 'deal',
    id: t.task_id,
    title: t.title || 'Untitled task',
    due: t.due_date || null,
    status: t.status,
    done: t.status === 'done',
    check: isDealTickToCheck(t),
    // my own tick waiting on my TC — someone else's tick I only watch is not mine to wait on
    waiting: ticked && t.to_check !== true && t.owned === true,
    broker: isWaitingOnBroker(t),
    // RW-3426: closing work on a deal before Under Contract — never overdue
    waitsForContract: t.waits_for_contract === true,
    owned: t.owned === true,
    watching: t.watching === true,
    place: { key: `deal:${t.deal_id}`, label: t.property_label || 'A deal', kind: 'deal' },
    person: t.primary_agent_uid ? { uid: t.primary_agent_uid, name: null } : null,
    link: dealTaskLink(t),
    canComplete: false, // deal ticks follow lib/dealTaskAccess on the row itself
    raw: t,
  };
}

const ONBOARDING_DONE = new Set(['complete', 'waived']);

/**
 * Where an onboarding task opens: a hire's own item on their checklist
 * (/onboarding?item=, scroll-and-flash — RW-1237); staff work on the hire's
 * page with that task's drawer open (?instance=&task=, RW-2257).
 */
export function onboardingTaskLink(r) {
  const item = encodeURIComponent(r.itemId || r.taskId || '');
  if (r.own) return `/onboarding?item=${item}`;
  return r.instanceId ? `/onboarding?instance=${encodeURIComponent(r.instanceId)}&task=${item}` : '/onboarding';
}

/** A dealsApi taskInbox onboarding row (myWork's shape + `own`) → an item. */
export function fromOnboardingRow(r) {
  const done = ONBOARDING_DONE.has(r.status);
  return {
    key: `onboarding:${r.taskId}`,
    source: 'onboarding',
    id: r.taskId,
    title: r.title || 'Onboarding task',
    due: r.dueDate || null,
    status: r.status,
    done,
    check: false,
    waiting: false,
    broker: false,
    owned: r.owned === true,
    watching: r.watching === true,
    place: {
      key: `onboarding:${r.instanceId || r.agentUid || 'unknown'}`,
      label: r.own ? 'Your onboarding' : (r.agentName ? `${r.agentName}’s onboarding` : 'Onboarding'),
      kind: 'onboarding',
    },
    person: r.agentUid || r.agentName ? { uid: r.agentUid || null, name: r.agentName || null } : null,
    link: onboardingTaskLink(r),
    // Complete (toggleOnboardingItemStatus) only where the server said its own
    // toggle rule allows it (taskInbox canComplete — canToggleItem), and only
    // on work that is theirs. A hire's own items are forms, signatures and
    // uploads — they open the checklist, where the item does its own thing.
    canComplete: !done && r.canComplete === true && r.owned === true && r.own !== true && !!r.instanceId,
    raw: r,
  };
}

const OTHER_DONE = new Set(['completed', 'complete', 'approved', 'waived', 'na', 'cancelled']);
const OTHER_MODULE_LABELS = { listings: 'Listing task', buyers: 'Buyer task', transactions: 'Transaction task', contacts: 'Follow-up' };

/**
 * An owner-scoped Firestore task ({ path, ...doc }) → an item, or null when it
 * isn't one to show (archived, or done more than a week ago). `label` and
 * `route` are its parent record's (useTaskParentLabels); `route` is null when
 * the route guard would bounce it (RW-2437).
 */
export function fromOtherTask(t, { uid, label = '', route = null, today }) {
  if (!t || !t.path || t.archived) return null;
  const done = OTHER_DONE.has(t.status);
  if (done) {
    const at = toDate(t.completedAt);
    if (!at || !today || localYmd(at) < addDaysYmd(today, -7)) return null;
  }
  const [module, parentId] = String(t.path).split('/');
  const due = toDate(t.dueDate);
  return {
    key: `task:${t.path}`,
    source: 'task',
    id: t.path,
    title: t.title || t.name || 'Untitled task',
    due: due ? localYmd(due) : null,
    status: t.status,
    done,
    check: false,
    waiting: false,
    broker: false,
    // assigned to me, or mine and nobody else's; one I handed to someone is
    // theirs (RW-3801: the same rule Other Tasks, Home and Focus Mode use)
    owned: isMyTask(t, uid),
    watching: false,
    place: { key: `${module}/${parentId}`, label: label || OTHER_MODULE_LABELS[module] || 'Task', kind: 'record', module },
    person: null,
    link: route || null,
    canComplete: !done,
    raw: t,
  };
}

/* ───────── buckets, filters, search, groups ───────── */

export const BUCKETS = [
  ['check', 'To check'],
  ['overdue', 'Overdue'],
  ['today', 'Today'],
  ['week', 'This week'],
  ['later', 'Later'],
  ['waiting', 'Waiting on your TC'],
  ['done', 'Done this week'],
];
const BUCKET_RANK = Object.fromEntries(BUCKETS.map(([k], i) => [k, i]));

/** Which bucket an item sits in today. Undated open work is Later. */
export function bucketOf(item, today) {
  if (item.done) return 'done';
  if (item.check) return 'check';
  if (item.waiting) return 'waiting';
  if (!item.due) return 'later';
  if (item.due < today) return item.waitsForContract ? 'later' : 'overdue'; // RW-3426
  if (item.due === today) return 'today';
  if (item.due <= addDaysYmd(today, 7)) return 'week';
  return 'later';
}

const SOURCE_RANK = { deal: 0, onboarding: 1, task: 2 };
/**
 * Most urgent first: bucket, then due date (undated last), then kind. Ties
 * keep the order they came in (a stable sort) — the server's own order.
 */
export function byUrgency(today) {
  return (a, b) => (BUCKET_RANK[bucketOf(a, today)] - BUCKET_RANK[bucketOf(b, today)])
    || String(a.due || '9999-12-31').localeCompare(String(b.due || '9999-12-31'))
    || (SOURCE_RANK[a.source] - SOURCE_RANK[b.source]);
}

/** The URL filter chips (?show=). */
export const SHOW_FILTERS = ['overdue', 'today', 'week', 'check', 'broker', 'mine'];

export function matchesShow(item, show, today) {
  switch (show) {
    case 'overdue':
    case 'today':
    case 'week':
      return bucketOf(item, today) === show;
    case 'check': return item.check;
    case 'broker': return item.broker;
    case 'mine': return item.owned && !item.done;
    default: return true;
  }
}

/** Search: every word somewhere in the title, the deal / hire / record, or the person. */
export function matchesQuery(item, q, nameOf = () => null) {
  const words = String(q || '').toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = [item.title, item.place && item.place.label, personName(item, nameOf)]
    .filter(Boolean).join(' ').toLowerCase();
  return words.every((w) => hay.includes(w));
}

function personName(item, nameOf) {
  if (!item.person) return null;
  return item.person.name || (item.person.uid ? nameOf(item.person.uid) : null) || null;
}

/**
 * The list, sectioned. group 'due' (the default): one section per bucket, in
 * BUCKETS order, empty ones left out. group 'place': one per deal / hire /
 * record. group 'person': one per agent (a deal's agent, a hire). Place and
 * person sections are ordered by their most urgent item. → [{ key, label, items }]
 */
export function sectionInbox(items, { today, group = 'due', nameOf = () => null } = {}) {
  const sorted = [...items].sort(byUrgency(today));
  if (group === 'due') {
    return BUCKETS.map(([key, label]) => ({ key, label, items: sorted.filter((i) => bucketOf(i, today) === key) }))
      .filter((s) => s.items.length);
  }
  const sections = [];
  const byKey = new Map();
  for (const item of sorted) {
    let key;
    let label;
    if (group === 'person') {
      const name = personName(item, nameOf);
      key = item.person && (item.person.uid || item.person.name) ? `person:${item.person.uid || item.person.name}` : 'person:none';
      label = name || (item.person ? 'An agent' : 'Not on a deal or hire');
    } else {
      key = item.place.key;
      label = item.place.label;
    }
    if (!byKey.has(key)) {
      const s = { key, label, items: [] };
      byKey.set(key, s);
      sections.push(s);
    }
    byKey.get(key).items.push(item);
  }
  return sections;
}

/**
 * The stat strip's and the chips' numbers — RW-3416: the rows the list shows.
 * Each bucket counts that section of the list (and what its chip filters to);
 * Total open counts every row not done. They counted only work I own while
 * the list also holds what I watch, so "Total open 123" sat over "Later · 144".
 */
export function inboxCounts(items, today) {
  const open = items.filter((i) => !i.done);
  const at = (k) => open.filter((i) => bucketOf(i, today) === k).length;
  return {
    overdue: at('overdue'),
    today: at('today'),
    week: at('week'),
    check: at('check'),
    broker: items.filter((i) => i.broker).length,
    open: open.length,
  };
}

/**
 * The nav badge: what needs me now — MY overdue work (never what I only watch
 * or handed to someone) and the ticks waiting on my check.
 */
export function inboxBadgeCount(items, today) {
  const mineOverdue = items.filter((i) => i.owned && !i.done && bucketOf(i, today) === 'overdue').length;
  return mineOverdue + items.filter((i) => !i.done && i.check).length;
}

/**
 * Home's queue rows (producerHomeData.buildQueue) for the deal and onboarding
 * tasks that need me today: ticks to check, overdue, due today. The Firestore
 * tasks reach the queue through the dashboard's own buckets, so they are not
 * repeated here. Windowed PER KIND (`perSource` each) so a TC's deal tasks can
 * never crowd a hire's onboarding out of the queue. Each opens its task.
 */
export function inboxQueueRows(items, today, { perSource = 3 } = {}) {
  const out = [];
  for (const source of ['deal', 'onboarding']) {
    const due = items
      .filter((i) => i.source === source && !i.done && i.link
        && (i.check || (i.owned && ['overdue', 'today'].includes(bucketOf(i, today)))))
      .sort(byUrgency(today))
      .slice(0, perSource);
    for (const i of due) {
      const bucket = bucketOf(i, today);
      const when = bucket === 'check' ? 'ticked — yours to check' : bucket === 'today' ? 'due today' : dueLabel(i.due, today);
      out.push({
        id: `inbox:${i.key}`,
        kind: 'task',
        tone: bucket === 'today' ? 'info' : 'err',
        icon: 'clip',
        weight: bucket === 'check' ? 85 : bucket === 'overdue' ? 80 : 50,
        // the due day's local midnight, so it sorts beside the dashboard's own task rows (local dates)
        sortAt: i.due ? localMidnight(i.due) : 0,
        title: i.title,
        sub: [i.place.label, when].filter(Boolean).join(' · '),
        age: bucket === 'overdue' ? `${daysBetween(i.due, today)}d late` : bucket === 'today' ? 'today' : 'to check',
        action: 'Open',
        to: i.link,
      });
    }
  }
  return out;
}
